/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { takeUntil } from 'rxjs/operators';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { cssVar } from '@touchplan/css-var';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { PromisePeriod } from '../../../../app-data/models/PromisePeriod';
import { ActiveLine } from '../../../../app-data/models/ActiveLine';
import { DefaultSplitDataRenderType } from '@touchplan/common';

const settings = {
	// The color of the last (previous) period
	lastPeriodColor: cssVar.promisePeriodLastColor,
	// The opacity of the last (previous) period
	lastPeriodAlpha: cssVar.promisePeriodLastAlpha,
	// The color of the next period
	nextPeriodColor: cssVar.promisePeriodNextColor,
	// The opacity of the next period
	nextPeriodAlpha: cssVar.promisePeriodNextAlpha,
	// The width of the boundary line. Pulled from old DOM code
	lineWidth: 4,
	// Magic number to reproduce the 1px space between the previous and next boundary lines from DOM
	boundaryOffset: -1,
};

/**
 * PromisePeriodBackgroundDrawer
 * This draws the promise period background boundary
 */
export class PromisePeriodBackgroundDrawer extends AbstractAreaDrawer {
	/**
	 * A cache of promise period service event emissions
	 * We use this to draw the previous and next periods
	 * @private
	 */
	private _cachedPromisePeriod: Map<string, PromisePeriod> | null = null;
	/**
	 * A cache of the active line. The reason why we collect this data is because we anchor our promise period at
	 * the active line position, then draw as an offset from the line.
	 * @private
	 */
	private _cachedActiveLine: ActiveLine | null = null;
	/**
	 *
	 * @param _canvasAngularLand
	 * @param parentApplication
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, parentApplication: AbstractApplication) {
		super(parentApplication, LayerPaths.BACKGROUND_VISUALS_PROMISE_PERIOD);
		this._container.eventMode = EventMode.Disabled;
		this.setupSubscribers();
	}
	/**
	 * Draw the promise period boundary lines
	 * @param promisePeriodData
	 */
	public override draw(
		promisePeriodData: Map<string, PromisePeriod> | null = this._cachedPromisePeriod
	): void {
		if (!promisePeriodData || !this._cachedActiveLine) {
			this.hide();
			return;
		}

		// If the active line is in edit mode, do not update the promise period because we anchor the period to the active line
		// this will cause the promise period to shift as we move the active line over to the pull side
		if (this._cachedActiveLine.currentRenderType === DefaultSplitDataRenderType.edit) {
			return;
		}

		super.draw();
		const lastId: string = this._canvasAngularLand.promisePeriodService.lastPromisePeriodId;
		const nextId: string = this._canvasAngularLand.promisePeriodService.currentPromisePeriodId;
		this.tryDrawPeriod(lastId, settings.lastPeriodColor, settings.lastPeriodAlpha);
		this.tryDrawPeriod(nextId, settings.nextPeriodColor, settings.nextPeriodAlpha);
	}
	/**
	 * A helper method to draw the period if required conditions are met
	 * @param periodId the promise period ID to draw
	 * @param color The color to draw
	 * @param alpha The opacity of the period
	 * @private
	 */
	private tryDrawPeriod(periodId: string, color: string, alpha: number): void {
		const period: PromisePeriod | undefined = this._cachedPromisePeriod?.get(periodId);
		if (!period || period.view.minimized) {
			return;
		}

		this.drawPeriod(period, color, alpha);
	}

	/**
	 * Updates the position of the container.
	 * @param _ The time since the last frame, not used.
	 */
	public override update(_?: number): void {
		if (!this._cachedActiveLine) {
			return;
		}

		// If the active line is in edit mode, do not update the promise period because we anchor the period to the active line
		// this will cause the promise period to shift as we move the active line over to the pull side
		if (this._cachedActiveLine.currentRenderType === DefaultSplitDataRenderType.edit) {
			return;
		}

		const activeLine: ActiveLine = this._cachedActiveLine;
		const screenX: number = this.parentApplication.camera.realToScreenX(activeLine.view.activeLineX);
		super.update(screenX, cssVar.headerHeight_px);
	}

	/**
	 * Destroy all the things. We need to override the abstract destroy so we can clear up our objects specific to this model
	 * @param options
	 */
	public override destroy(options: DestroyOptions = DestroyOptions.includeChildren): void {
		super.destroy(options);
	}
	/**
	 * Setup all internal subscriptions that causes this drawer to re-draw
	 * @private
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand.promisePeriodService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((periods: Map<string, PromisePeriod> | null) => {
				this._cachedPromisePeriod = periods;
				this.draw();
			});

		this._canvasAngularLand.activeLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((activeLine) => {
				this._cachedActiveLine = activeLine;
				this.update();
				this.draw();
			});

		this.parentApplication.camera.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((changedEvent) => {
				this.update();
				this.draw();
			});
	}

	/**
	 * Draws the promise period boundary lines.
	 * @param promisePeriod The PromisePeriod model to draw
	 * @param color The color to draw this promise period
	 * @param alpha the alpha value for this promise period
	 * @private
	 */
	private drawPeriod(
		promisePeriod: PromisePeriod,
		color: string,
		alpha: number = settings.lastPeriodAlpha
	): void {
		const startWorldX: number =
			this._canvasAngularLand.activeColumnService.getXFromDate(promisePeriod.view.startDate as string) -
			this._canvasAngularLand.activeColumnService.columnWidth;
		const endWorldX: number = this._canvasAngularLand.activeColumnService.getXFromDate(
			promisePeriod.view.endDate as string
		);

		const startScreenX: number = this.parentApplication.camera.realToScreenX(startWorldX);
		const endScreenX: number = this.parentApplication.camera.realToScreenX(endWorldX);

		this._graphics.beginFill(color);
		this._graphics.alpha = alpha;
		// Draws the left boundary edge
		this._graphics.drawRect(
			startScreenX - this._container.x,
			cssVar.timelineHeight_px,
			settings.lineWidth,
			this.parentApplication.camera.view.height
		);

		// Draws the right boundary edge
		this._graphics.drawRect(
			// Old code maintained a 1px separation between the previous and next boundary lines for the promise period
			// So: add the inverse of the lineWidth (to shift in the opposite direction) plus a 1px offset
			// Then we draw relative to our anchor point, which is the active line (container.x)
			endScreenX - this._container.x + -settings.lineWidth + settings.boundaryOffset,
			cssVar.timelineHeight_px,
			settings.lineWidth,
			this.parentApplication.camera.view.height
		);
		this._graphics.endFill();
	}
}
